/** Client for the recipe editor API (task 4090.4). */
import { apiFetch } from "../../app/api";

const BASE = "/api/v1/recipes";

export interface FamilyRow {
  id: string;
  family_key: string;
  name: string;
  family_state: string;
}

export interface RecipeField {
  field_name: string;
  meaning: string | null;
  source_anchor_pattern: Record<string, unknown>;
  unit: string | null;
  concept_key: string | null;
}

export interface RecipeGovernance {
  known_exclusions: string[];
  proof_limits: string[];
  revalidation_triggers: string[];
  rollback_path: string | null;
}

export interface RegressionReport {
  passed: boolean;
  cases: { case_id: string; description: string; outcome: string; detail: string }[];
}

export type LifecycleMove = "activate" | "suspend" | "resume" | "retire";

export interface RecipeVersion {
  id: string;
  recipe_key: string;
  version: number;
  lifecycle_state: string;
  family_id: string;
  owner_party: string;
  bounded_condition: string;
  fields: RecipeField[];
  test_case_count: number;
  approved_by: string | null;
  proposed_by: string;
  is_current: boolean;
  governance: RecipeGovernance;
}

export interface FieldResolution {
  source_artifact_version_id: string;
  representative_role: string;
  parsed_document_id: string | null;
  resolved: { region_id: string; region_key: string; match: string } | null;
}

export interface FieldPreview {
  field_name: string;
  source_anchor_pattern: Record<string, unknown>;
  resolutions: FieldResolution[];
  resolved_somewhere: boolean;
}

export interface RecipePreview {
  recipe_version_id: string;
  fields: FieldPreview[];
  all_fields_resolved: boolean;
}

export interface TestCaseInput {
  case_kind: "clean" | "edge" | "negative";
  description: string;
  input: unknown;
  expected: unknown;
}

export interface RecipeException {
  id: string;
  source_artifact_version_id: string;
  exception_kind: "deviates" | "low_fit" | "failed";
  reasons: { code: string; detail: string; field_name?: string | null }[];
  closest_recipe_version_id: string | null;
  closest_fit_score: string | number | null;
  no_closest_statement: string | null;
  routed_at: string;
}

export function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

function send<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export function listFamilies(engagementId: string): Promise<FamilyRow[]> {
  return apiFetch<FamilyRow[]>(
    `${BASE}/families?${new URLSearchParams({ engagement_id: engagementId }).toString()}`,
  );
}

export function listVersions(familyId: string): Promise<RecipeVersion[]> {
  return apiFetch<RecipeVersion[]>(`${BASE}?${new URLSearchParams({ family_id: familyId }).toString()}`);
}

export function listExceptions(familyId: string): Promise<RecipeException[]> {
  return apiFetch<RecipeException[]>(
    `${BASE}/exceptions?${new URLSearchParams({ family_id: familyId }).toString()}`,
  );
}

export function getVersion(versionId: string): Promise<RecipeVersion> {
  return apiFetch<RecipeVersion>(`${BASE}/${encodeURIComponent(versionId)}`);
}

export function getPreview(versionId: string): Promise<RecipePreview> {
  return apiFetch<RecipePreview>(`${BASE}/${encodeURIComponent(versionId)}/preview`);
}

export function saveVersion(body: {
  family_id: string;
  fields: RecipeField[];
  owner_party: string;
  governance: RecipeGovernance;
  reason: string;
}): Promise<RecipeVersion> {
  return send<RecipeVersion>(BASE, body);
}

export function addTestCase(versionId: string, body: TestCaseInput): Promise<RecipeVersion> {
  return send<RecipeVersion>(`${BASE}/${encodeURIComponent(versionId)}/test-cases`, body);
}

export function approveVersion(versionId: string, reason: string): Promise<RecipeVersion> {
  return send<RecipeVersion>(`${BASE}/${encodeURIComponent(versionId)}/approve`, { reason });
}

export function runRegression(versionId: string): Promise<RegressionReport> {
  return send<RegressionReport>(`${BASE}/${encodeURIComponent(versionId)}/regression`, {});
}

export function moveVersion(
  versionId: string,
  move: LifecycleMove,
  reason: string,
): Promise<RecipeVersion> {
  return send<RecipeVersion>(`${BASE}/${encodeURIComponent(versionId)}/lifecycle/${move}`, { reason });
}

export function rollbackTo(versionId: string, reason: string): Promise<RecipeVersion> {
  return send<RecipeVersion>(`${BASE}/${encodeURIComponent(versionId)}/rollback`, { reason });
}
